import React from "react";
import {toast} from "react-toastify";

export const formatAnnoucementStatusAsPill = (status) => {
    const pillStyle = {
        padding: '4px 12px',
        borderRadius: '16px',
        fontSize: '14px',
        fontWeight: '500',
        display: 'inline-block'
    };
    switch (status) {
        case 'active':
            return <span style={{ ...pillStyle, backgroundColor: '#ECFDF3', color: '#027A48' }}>Aktivan</span>;
        case 'inactive':
            return <span style={{ ...pillStyle, backgroundColor: '#EFF8FF', color: '#175CD3' }}>Neaktivan</span>;
        default:
            return <span style={{ ...pillStyle, backgroundColor: '#F2F4F7', color: '#344054' }}>Nepoznat status</span>;
    }
};

export const ANNOUCEMENT_FORM = {
    // Step 1
    _id: '',
    name: '',
    status: 'active',
    displayName: {
        sr: '',
        hu: '',
        en: '',
    },
    displayDescription:{
        sr: undefined,
        hu: undefined,
        en: undefined,
    },
    displayText:{
        sr: '',
        hu: '',
        en: '',
    },
    link: undefined,
    imageUrl: undefined,
    mediaType: 'image',
    videoUrl: undefined,

    // Step 2
    displayInPage: '',
    startDate: undefined,
    endDate: undefined,
    displayFrequency: undefined,
    targetUserGroup: '',
}

export const ANNOUCEMENT_FILTER_OPTIONS = [
    { label: 'Aktivan', value: 'active' },
    { label: 'Neaktivan', value: 'inactive' },
];

export const ANNOUNCEMENT_STATUS_OPTIONS = [
    { value: 'active', label: 'Aktivan' },
    { value: 'inactive', label: 'Neaktivan' }
];

export const MEDIA_TYPE_OPTIONS = [
    { value: 'image', label: 'Slika' },
    { value: 'video', label: 'Video' },
];

export const DISPLAY_PAGE_OPTIONS = [
    { value: 'webshop-page', label: 'Webshop' },
    { value: 'reminder-page', label: 'Podsetnik' },
    { value: 'ai-page', label: 'AI' },
    { value: 'our-team-page', label: 'Naš tim' },
    { value: 'my-profile-page', label: 'Moj profil' },
];

export const DISPLAY_FREQUENCY_OPTIONS = [
    { value: 'once', label: 'Jednom' },
    { value: 'daily', label: 'Dnevno' },
    { value: 'weekly', label: 'Nedeljno' },
    { value: 'monthly', label: 'Mesečno' },
    { value: 'every-time', label: 'Svaki put (pri otvaranju aplikacije)' }
];

// "Svaki put" is only valid for the app-open trigger, not a regular page -
// the API rejects any other combination, so the two are kept in lockstep in the UI.
export const EVERY_TIME_FREQUENCY = 'every-time';
export const APP_OPEN_PAGE = 'app-open-page';
export const APP_OPEN_PAGE_OPTION = { value: APP_OPEN_PAGE, label: 'Otvaranje aplikacije (automatski)' };

// Keeps displayInPage in sync with displayFrequency: forces app-open-page when
// every-time is selected, and clears it if every-time is deselected.
export const syncDisplayInPageWithFrequency = (formData, updateFormData) => {
    if (formData.displayFrequency === EVERY_TIME_FREQUENCY && formData.displayInPage !== APP_OPEN_PAGE) {
        updateFormData('displayInPage', APP_OPEN_PAGE);
    } else if (formData.displayFrequency !== EVERY_TIME_FREQUENCY && formData.displayInPage === APP_OPEN_PAGE) {
        updateFormData('displayInPage', '');
    }
};

export const TARGET_USER_GROUP_OPTIONS = [
    { value: 'all', label: 'Svi korisnici' },
    { value: 'new-user', label: 'Novi korisnici' },
];

export const validatedFirstStep = (formData) => {
    let error = false;

    if (!formData.name || formData.name.trim() === '') {
        toast.error('Interni naziv ne sme biti prazan.');
        error = true;
    }
    if (!formData.displayName.sr || !formData.displayName.hu || !formData.displayName.en) {
        toast.error('Naslov mora biti popunjen na svim jezicima.');
        error = true;
    }
    if (!formData.displayText.sr || !formData.displayText.hu || !formData.displayText.en) {
        toast.error('Tekst objave mora biti popunjen na svim jezicima.');
        error = true;
    }
    if (!validateMultilingualOptional(formData.displayDescription, 'Opis')) {
        error = true;
    }
    if (formData.mediaType === 'video' && !formData.videoUrl) {
        toast.error('Video mora biti otpremljen.');
        error = true;
    }
    if (formData.mediaType !== 'video' && !formData.imageUrl) {
        toast.error('Slika mora biti otpremljena.');
        error = true;
    }
    return !error;
}

const validateMultilingualOptional = (data, fieldLabel) => {
    if (!data) return true;

    const { sr, hu, en } = data;
    
    const isAnyPopulated = (sr && sr.trim() !== '') ||
        (hu && hu.trim() !== '') ||
        (en && en.trim() !== '');

    if (isAnyPopulated) {
        if (!sr || sr.trim() === '') {
            toast.error(`${fieldLabel} ne sme biti prazan (Srpski).`);
            return false;
        }
        if (!hu || hu.trim() === '') {
            toast.error(`${fieldLabel} ne sme biti prazan (Mađarski).`);
            return false;
        }
        if (!en || en.trim() === '') {
            toast.error(`${fieldLabel} ne sme biti prazan (Engleski).`);
            return false;
        }
    }
    return true;
};

export const validatedSecondStep = (formData) => {
    let error = false;

    const isEmpty = (val) => val === undefined || val === null || val === '';

    if (isEmpty(formData.displayInPage)) {
        toast.error('Izabrana stranica ne sme biti prazna.');
        error = true;
    }
    if (!formData.startDate) {
        toast.error('Datum starta ne sme biti prazan.');
        error = true;
    }
    if (!formData.endDate) {
        toast.error('Datum kraja ne sme biti prazan.');
        error = true;
    }
    if (formData.startDate && formData.endDate) {
        const start = new Date(formData.startDate);
        const end = new Date(formData.endDate);
        const now = new Date();
        now.setHours(0, 0, 0, 0);

        const startCheck = new Date(start);
        startCheck.setHours(0, 0, 0, 0);
        
        if (start > end) {
            toast.error('Datum kraja mora biti nakon datuma starta.');
            error = true;
        }
    }
    if (!formData.displayFrequency) {
        toast.error('Frekvencija prikaza ne sme biti prazna.');
        error = true;
    }
    if (isEmpty(formData.targetUserGroup)) {
        toast.error('Grupa korisnika ne sme biti prazna.');
        error = true;
    }
    return !error;
};

export const validatedForm = (formData) => {
    if (!validatedFirstStep(formData)){
        return false;
    }
    else if (!validatedSecondStep(formData)){
        return false;
    }
    return true;
}

